import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, PieChart,
  Calendar, FileText, DollarSign, ChevronRight, RefreshCw
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount, currency = 'SEK') => {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function MobileReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('overview');
  const [reportData, setReportData] = useState({
    income: 0,
    expenses: 0,
    profit: 0,
    vat: { output: 0, input: 0, toPay: 0 },
    topCustomers: [],
    monthlyRevenue: []
  });

  const token = localStorage.getItem('token');
  const currentYear = new Date().getFullYear();

  const fetchReportData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [incomeRes, vatRes] = await Promise.all([
        fetch(`${API_BASE}/api/reports/income-statement?year=${currentYear}`, { headers }),
        fetch(`${API_BASE}/api/reports/vat-summary?year=${currentYear}`, { headers })
      ]);

      const incomeData = incomeRes.ok ? await incomeRes.json() : {};
      const vatData = vatRes.ok ? await vatRes.json() : {};

      setReportData({
        income: incomeData.total_revenue || 0,
        expenses: incomeData.total_expenses || 0,
        profit: incomeData.net_profit || 0,
        vat: {
          output: vatData.output_vat || 0,
          input: vatData.input_vat || 0,
          toPay: vatData.vat_to_pay || 0
        },
        topCustomers: incomeData.top_customers || [],
        monthlyRevenue: incomeData.monthly_revenue || []
      });
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentYear]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const reportTypes = [
    { id: 'income', icon: <TrendingUp size={20} />, label: t('reports.incomeStatement'), color: '#dcfce7', iconColor: '#16a34a' },
    { id: 'balance', icon: <PieChart size={20} />, label: t('reports.balanceSheet'), color: '#e0f2fe', iconColor: '#0284c7' },
    { id: 'vat', icon: <DollarSign size={20} />, label: t('reports.vat'), color: '#f3e8ff', iconColor: '#9333ea' },
    { id: 'sales', icon: <BarChart3 size={20} />, label: t('reports.salesByMonth'), color: '#ffedd5', iconColor: '#ea580c' },
  ];

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('reports.title')}</div>
        </div>
        <button className="mobile-header-btn" onClick={fetchReportData} disabled={loading}>
          <RefreshCw size={20} className={loading ? 'mobile-spinner' : ''} />
        </button>
      </header>

      {/* Main Content */}
      <main className="mobile-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : (
          <>
            {/* Period Selector */}
            <div className="mobile-card" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={18} color="#64748b" />
                  <span style={{ fontWeight: 500 }}>{currentYear}</span>
                </div>
                <span style={{ color: '#64748b', fontSize: '13px' }}>Helår</span>
              </div>
            </div>

            {/* Overview Stats */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">Översikt {currentYear}</h3>
            </div>
            <div className="mobile-stats-grid">
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon green">
                  <TrendingUp size={24} />
                </div>
                <div className="mobile-stat-value">{formatCurrency(reportData.income)}</div>
                <div className="mobile-stat-label">{t('reports.totalRevenue')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon red">
                  <TrendingDown size={24} />
                </div>
                <div className="mobile-stat-value">{formatCurrency(reportData.expenses)}</div>
                <div className="mobile-stat-label">{t('reports.totalExpenses')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon blue">
                  <BarChart3 size={24} />
                </div>
                <div className="mobile-stat-value" style={{ color: reportData.profit >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatCurrency(reportData.profit)}
                </div>
                <div className="mobile-stat-label">{t('reports.netProfit')}</div>
              </div>
              <div className="mobile-stat-card">
                <div className="mobile-stat-icon purple">
                  <DollarSign size={24} />
                </div>
                <div className="mobile-stat-value">{formatCurrency(reportData.vat.toPay)}</div>
                <div className="mobile-stat-label">{t('reports.vatToPay')}</div>
              </div>
            </div>

            {/* Report Types */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">Rapporter</h3>
            </div>
            <div className="mobile-list">
              {reportTypes.map((report) => (
                <div 
                  key={report.id}
                  className="mobile-list-item"
                  onClick={() => navigate(`/reports?tab=${report.id}`)}
                >
                  <div className="mobile-list-icon" style={{ background: report.color, color: report.iconColor }}>
                    {report.icon}
                  </div>
                  <div className="mobile-list-content">
                    <div className="mobile-list-title">{report.label}</div>
                  </div>
                  <ChevronRight size={20} color="#94a3b8" />
                </div>
              ))}
            </div>

            {/* VAT Summary */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">{t('reports.vat')}</h3>
            </div>
            <div className="mobile-card">
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b' }}>{t('reports.outputVat')}</span>
                  <span style={{ fontWeight: 600 }}>{formatCurrency(reportData.vat.output)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b' }}>{t('reports.inputVat')}</span>
                  <span style={{ fontWeight: 600 }}>-{formatCurrency(reportData.vat.input)}</span>
                </div>
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{t('reports.vatToPay')}</span>
                  <span style={{ fontWeight: 700, fontSize: '18px', color: reportData.vat.toPay > 0 ? '#dc2626' : '#16a34a' }}>
                    {formatCurrency(reportData.vat.toPay)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button 
                className="mobile-btn mobile-btn-outline"
                onClick={() => navigate('/reports?tab=sie')}
                style={{ flex: 1 }}
              >
                <FileText size={18} />
                SIE Export
              </button>
              <button 
                className="mobile-btn mobile-btn-primary"
                onClick={() => navigate('/reports')}
                style={{ flex: 1 }}
              >
                Alla rapporter
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
